import { routingStore } from "app";
import { IconId } from "components";
import client from "feathers";
import i18next from "i18n";
import { action, computed, IReactionDisposer, makeObservable, observable, reaction, runInAction } from "mobx";
import { computedFn } from "mobx-utils";
import { HierarchyTypes, quantile, Services, sum, TreeData, TreeDataItem, TreeLinks } from "types";

export const hierarchyIconIds: Record<HierarchyTypes, IconId> = {
  [HierarchyTypes.Company]: "company",
  [HierarchyTypes.Program]: "programs",
  [HierarchyTypes.Course]:  "book",
  [HierarchyTypes.User]:    "user",
  [HierarchyTypes.Quiz]:    "quiz",
}

const default_hierarchy: HierarchyTypes[] = [HierarchyTypes.Course, HierarchyTypes.User];
export class Tree {
  hierarchy: HierarchyTypes[] = [];
  data:      TreeData         = {};
  links:     TreeLinks        = [];

  // Settings and filters
  show_empty_chart:  boolean = false;
  show_empty_tree:   boolean = false;
  show_fake_data:    boolean = false;
  time_scale:       "m" | "h" | "d" = "m";

  protected static readonly TimeScales = {
    "m": 60,
    "h": 60*60,
    "d": 60*60*24,
  }
  protected readonly decial_places = 2;

  // Private members that ensure data integrity 
  has_filter: number = 0;
  load_id:    number = 0; // Used to ensure that only the last load() changes state
  is_loading: number = 0; // prevents state update from hierarchy change to return invalid children until data was loaded

  private disposer: IReactionDisposer = null;
  private readonly _DEBUG: boolean = false;
  constructor(values?: {hierarchy?: HierarchyTypes[], data?: TreeData, links?: TreeLinks}) {
    makeObservable(this, {
      hierarchy:         observable,
      data:              observable,
      links:             observable,
      load_id:           observable,
      has_filter:        observable,
      is_loading:        observable,
      time_scale:        observable,
      show_empty_chart:  observable,
      show_empty_tree:   observable,
      show_fake_data:    observable,

      getRootData:   computed,
      getTopLevel:   computed,
      getBottomType: computed,

      setHierarchy:          action,
      setData:               action,
      setLinks:              action,
      check:                 action,
      destroy:               action,
      setTimeScale:          action,
      toggleShowEmptyChart:  action,
      toggleShowEmptyTree:   action,
      toggleShowFakeData:    action,
    });
    this.setHierarchy(values.hierarchy);
    values?.data      && this.setData(values.data);
    values?.links     && this.setLinks(values.links);

    // Update data when hierarchy changes
    this.disposer = reaction(() => this.hierarchy, this.load);
  }

  destroy = (): void => {
    // Ensure that the hierarchy change does not trigger a load()
    this.disposer();

    this.data       = {};
    this.links      = [];
    this.has_filter = 0;
    this.load_id    = 0;
    this.is_loading = 0;
    this.setHierarchy();

    // Watch hierarchy changes to trigger loads again
    this.disposer = reaction(() => this.hierarchy, this.load);
  }
  load = async (): Promise<boolean> => {
    try {
      runInAction(() => {
        this.data       = {};
        this.links      = [];
        this.has_filter = 0;
      });
      if(!this.hierarchy || !this.hierarchy.length)
        return true;
        
      routingStore.startLoading();
      let _load_id: number;
      runInAction(() => _load_id = ++this.load_id);
      const {treeData, treeLinks } = await client.service(Services.Statistics).getTreeData(this.hierarchy);
      routingStore.finishLoading();
      runInAction(() => {
        this.is_loading = this.is_loading ? this.is_loading - 1 : 0;
        if(_load_id === this.load_id){
          this.data  = treeData;
          this.links = treeLinks;
        }
      });
      return true;
    } catch(err) {
      runInAction(() => this.is_loading = this.is_loading ? this.is_loading - 1 : 0);
      routingStore.finishLoading();
      console.error("Failed to load statistics", err);
      return false;
    }
  }

  toggleShowEmptyChart = () => {
    this.show_empty_chart = !this.show_empty_chart;
  }
  
  toggleShowEmptyTree = () => {
    this.show_empty_tree = !this.show_empty_tree;
  }

  toggleShowFakeData = (showFake?: boolean) => {
    this.show_fake_data = showFake ? showFake : !this.show_fake_data;
  }

  get getRootData() {
    if(!this.hierarchy || !this.data || !this.hierarchy.length) return [];
    return this.data[this.hierarchy[0]] ? Object.values(this.data[this.hierarchy[0]]) : [];
  }
  setData = (data: TreeData) => {
    this.data = data;
  }
  setHierarchy = (h?: HierarchyTypes[]): void => {
    this.is_loading++;
    this.hierarchy = h || default_hierarchy;
  }
  setLinks = (l: TreeLinks): void => {
    this.links = l;
  }

  hasFullDepth = computedFn((depth: number, tree_id: number): boolean => {
    if(depth >= (this.hierarchy?.length-1)) return true;
    if(!this.links[depth] || !this.links[depth][tree_id]?.length) return false;
    return this.links[depth][tree_id]?.some(l => this.hasFullDepth(depth+1, l));
  })

  getChildren = computedFn((depth: number, tree_id: number): TreeDataItem[] => {
    if(this.is_loading) return [];

    if (
      depth < this.hierarchy.length - 1 &&
      this.links.length > depth &&
      this.links[depth][tree_id]
    ) {
      return this.links[depth][tree_id].map(
          (id) => this.data[this.hierarchy[depth + 1]][id]
      ).filter(Boolean);
    } else {
      return [];
    }
  }, true);

  getDepth = computedFn((type: HierarchyTypes): number => {
    return this.hierarchy.findIndex(h => h === type);
  }, true);

  /**
   * Set the filter for a tree node to decide whether its data is included in the charts.
   * This is cascading down all children by default. 
   */
  check = (depth: number, tree_id: number, checked?: boolean) => {
    const target = checked != undefined ? checked : !this.data[this.hierarchy[depth]][tree_id].checked;
    this.data[this.hierarchy[depth]][tree_id].checked = target;
    target ? this.has_filter++ : this.has_filter--;
    
    if(depth < this.hierarchy.length-1) {
      const children = this.getChildren(this.getDepth(this.hierarchy[depth]), tree_id);
      children.forEach(c => this.check(depth+1, c.tree_id, target));
    }
  }

  get getTopLevel(): TreeDataItem[] {
    if(this.is_loading) return [];
    if(!Object.keys(this.data)?.length) return [];
    if(!this.has_filter) return Object.values(this.data[this.hierarchy[0]]);

    for(let i = 0; i < this.hierarchy.length; i++) {
      if(Object.values(this.data[this.hierarchy[i]]).find(item => item.checked)) {
        return Object.values(this.data[this.hierarchy[i]]).filter(item => item.checked);
      }
    }
    console.error("Failed to find top level tree items");
    return [];
  }

  get getBottomType(): HierarchyTypes {
    if(this.is_loading) return this.hierarchy[0];

    if(!this.has_filter) {
      for(let i = this.hierarchy.length-1; i >= 0; i--) {
        if(Object.values(this.data[this.hierarchy[i]]).length)
          return this.hierarchy[i];
      }
    };

    for(let i = this.hierarchy.length-1; i >= 0; i--) {
      if(Object.values(this.data[this.hierarchy[i]]).find(item => item.checked)) {
        return this.hierarchy[i];
      }
    }
    console.error("Failed to find bottom level type", this.hierarchy, this.data);
    return this.hierarchy[this.hierarchy.length-1];
  }

  getChartLabels = (): string[] => {
    if(this.is_loading) return [];
    return this.getTopLevel.map(item =>item.label);
  }

  getBottomChildren = (item: TreeDataItem): TreeDataItem[] => {
    if(this.is_loading) return [];

    let res: TreeDataItem[] = [item];
    let depth: number       = this.getDepth(item.hierarchy_type);

    while(res.length && res[0].hierarchy_type !== this.getBottomType) {
      const queue = [...res];
      res = [];
      while(queue.length) {
        const head = queue.pop();
        const children = this.getChildren(depth, head.tree_id)
        if(children && children.length) res.push(...children);
      }
      depth++;
    }
    return res;
  }

  getChartData = computedFn((chart_type: 'attempts' | 'duration' | 'status'): {data: ApexAxisChartSeries, labels: string[]} => {
    if(this._DEBUG) console.log("Start loading chart data. Is still loading", this.is_loading);
    if(Boolean(this.is_loading)) return {data: [], labels: []};
    let data: ApexAxisChartSeries, labels: string[];

    const top_level: TreeDataItem[] = this.getTopLevel;

    if(this._DEBUG) console.log("hierarchy", this.hierarchy.map(h => h));

    if(this._DEBUG) console.log("Top level", 
      top_level?.length, 
      top_level?.length ? top_level[0].hierarchy_type : undefined,
      top_level?.map(i => i.label)
    );

    const children:  {[tree_id: number]: TreeDataItem[]} = 
      top_level.reduce((res: {[tree_id: number]: TreeDataItem[]}, val: TreeDataItem) => {
        res[val.tree_id] = this.getBottomChildren(val);
        return res;
      }, {});

    if(this._DEBUG) console.log("Children", Object.keys(children)?.length, Object.values(children)?.map(i => i?.map(ti => ti.label)));

    // Filter out entries that do not have children or do not have valid data
    // In the case of a time chart, that means no duration thats not null
    const filtered_top = this.show_empty_chart ? top_level : top_level.filter(item => {
      const filter: Boolean = 
        Boolean(children[Number(item.tree_id)].length) && 
        (chart_type !== 'duration' || children[Number(item.tree_id)].some(item => item.duration != null));

      if(!filter) delete children[Number(item.tree_id)];

      return filter;
    });

    if(this._DEBUG) console.log("filtered_top", filtered_top?.length, filtered_top?.map(i => i.label));

    const extractAttemptNumbers = (num_attempts: number, and_more?: boolean) => {
      if(and_more) return Object.values(children).map(list => list.filter(item => Math.round(item.attempts) >= num_attempts).length);
      return Object.values(children).map(list => list.filter(item => Math.round(item.attempts) === num_attempts).length);
    }
    const extractStatus = (id: number, target: "not_started" | "active" | "completed"): number => {
      const getStatus = (item: TreeDataItem): "not_started" | "active" | "completed" => {
        if(item.progress === 0)   return "not_started";
        if(item.progress === 100) return "completed";
        return "active";
      }
      return children[id].filter(item => getStatus(item) === target).length;
    }

    const extractDurations = (item: TreeDataItem): number[] => {
      return children[item.tree_id] ? children[item.tree_id]
        .filter(item => item.duration != null)
        .map(item => {
        if(this._DEBUG) console.log("Item Duration Interval", item.duration,Tree.TimeScales[this.time_scale], item.duration / Tree.TimeScales[this.time_scale]);
        if(!item.duration) return 0;

        return (item.duration / Tree.TimeScales[this.time_scale]);
      }) : [];
    }

    if(this.show_fake_data && filtered_top.length && 
      [HierarchyTypes.User, HierarchyTypes.Company].includes(filtered_top[0].hierarchy_type)
    ) { 
      labels = filtered_top.map(item => 
          filtered_top[0].hierarchy_type === HierarchyTypes.User ? i18next.t('fake_user_name') + ' ' + item.tree_id 
        : filtered_top[0].hierarchy_type === HierarchyTypes.Company ? i18next.t('fake_company_name') + ' ' + item.tree_id 
        : item.label)
    } else {
      labels = filtered_top.map(item => item.label)
    }
    labels = [i18next.t('total'), ...labels];

    switch (chart_type) {
      case "attempts": 
        const nums_5 = extractAttemptNumbers(5, true);
        data = [
          ...[0, 1, 2, 3, 4].map(n => {
            const nums_n = extractAttemptNumbers(n);
            return {
              name: ""+n,
              data: [sum(nums_n), ...nums_n]
            }
          }),{
            name: "5+",
            data: [sum(nums_5), ...nums_5],
          },
        ];
        break;
      case "status": 
        const not_started = Object.keys(children).map(k => extractStatus(Number(k), "not_started"));
        const active      = Object.keys(children).map(k => extractStatus(Number(k), "active"));
        const completed   = Object.keys(children).map(k => extractStatus(Number(k), "completed"));
        data = [
          {
            name: i18next.t("not_started"),
            data: [sum(not_started), ...not_started],
          },{
            name: i18next.t("active"),
            data: [sum(active), ...active],
          },{
            name: i18next.t("completed"),
            data: [sum(completed), ...completed],
          },
        ];
        break;
      case "duration": 
        data = [{
            data: filtered_top.map((item) => {
              const durations: number[] = extractDurations(item).sort((a, b) => a - b);
              const label = this.show_fake_data && filtered_top[0].hierarchy_type === HierarchyTypes.User ? i18next.t('fake_user_name') + ' ' + item.tree_id 
              : this.show_fake_data && filtered_top[0].hierarchy_type === HierarchyTypes.Company ? i18next.t('fake_company_name') + ' ' + item.tree_id 
              : item.label;
              
              if(!durations?.length) 
                return {
                  x: label, y: [0, 0, 0, 0, 0]
                };
              if(durations.length < 2) 
                return {
                  x: label, y: new Array(5).fill(durations[0].toFixed(this.decial_places))
                };
              const m:   number = quantile(durations, 0.5);
              const q1:  number = quantile(durations, 0.25);
              const q3:  number = quantile(durations, 0.75);
              const min: number = Math.min(...durations);
              const max: number = Math.max(...durations);
              return {
                x: label, y: [min.toFixed(this.decial_places), q1.toFixed(this.decial_places), m.toFixed(this.decial_places), q3.toFixed(this.decial_places), max.toFixed(this.decial_places)]
              }
          })
        }];
        break;
      default: break;
    }
    if(this._DEBUG) console.log("Data", data);
    return {data, labels}
  });

  setTimeScale = (scale: "m" | "h" | "d") => {
    this.time_scale = scale;
  }
}
